import { defineStore } from "pinia";
import { ref } from "vue";

export type ToastTheme = "info" | "warning" | "danger" | "success";
interface Toast {
   /** Notification content */
   text: string;
   /** Colour theme */
   theme: ToastTheme;
   /** Id is auto generated by store */
   id: string;
}

type ToastOptions = { timeout?: number };

interface ToastPayload extends ToastOptions {
   text: string;
}

const defaultTimeout: number = 5000;

const useToastStore = defineStore("toast-store", () => {
   const toasts = ref<Toast[]>([]);

   function generateToast(text: string, theme: ToastTheme): Toast {
      return {
         text,
         theme,
         id: `toast-${Math.random() * 1000}`,
      };
   }

   function makeToast(payload: ToastPayload, status: ToastTheme) {
      const { timeout, text } = payload;

      const removeToastAfter = timeout ?? defaultTimeout;
      const toast = generateToast(text, status);
      toasts.value.push(toast);

      setTimeout(() => {
         toasts.value = toasts.value.filter((t) => t.id !== toast.id);
      }, removeToastAfter);
   }

   function info(text: string, options?: ToastOptions) {
      makeToast({ text, ...options }, "info");
   }

   function success(text: string, options?: ToastOptions) {
      makeToast({ text, ...options }, "success");
   }

   function error(text: string, options?: ToastOptions) {
      makeToast({ text, ...options }, "danger");
   }

   function warning(text: string, options?: ToastOptions) {
      makeToast({ text, ...options }, "warning");
   }

   return {
      toasts,
      makeToast,
      info,
      success,
      error,
      warning,
   };
});

export { useToastStore, defaultTimeout, type Toast };
